<template>
  <div class="conversation-list">
    <div class="pa-3">
      <v-btn
        block
        class="uwill-primary-button shadowed new-message-btn"
        @click="inbox.openPicker()"
      >
        <v-icon class="pr-2">fa-solid fa-plus</v-icon>
        New Message
      </v-btn>
    </div>

    <div class="d-flex mb-3 filter-row">
      <v-btn-toggle
        v-model="showUnreadOnly"
        mandatory
        class="segmented-toggle flex-grow-1"
      >
        <v-btn :value="true" class="flex-grow-1">Unread</v-btn>
        <v-btn :value="false" class="flex-grow-1">All</v-btn>
      </v-btn-toggle>
      <v-menu v-model="filterMenu" :close-on-content-click="false">
        <template #activator="{ props }">
          <v-btn v-bind="props" variant="outlined" class="filter-btn ml-2">
            <v-icon class="pr-2 mr-1" size="small">fa-regular fa-filter</v-icon>
            Filter
          </v-btn>
        </template>
        <v-card min-width="220" class="pa-3 filter-menu-card">
          <v-radio-group
            v-model="product"
            label="Product"
            density="compact"
            @update:model-value="applyFilters"
          >
            <v-radio label="All" :value="null"></v-radio>
            <v-radio label="Therapy" value="umatch"></v-radio>
            <v-radio label="Basic Needs" value="uthrive"></v-radio>
          </v-radio-group>
          <v-radio-group
            v-if="product === 'uthrive'"
            v-model="caseState"
            label="Case status"
            density="compact"
            @update:model-value="applyFilters"
          >
            <v-radio label="All" :value="null"></v-radio>
            <v-radio label="Open" value="open"></v-radio>
            <v-radio label="Closed" value="closed"></v-radio>
          </v-radio-group>
        </v-card>
      </v-menu>
    </div>

    <div class="search-wrapper pa-3">
      <v-text-field
        v-model="search"
        placeholder="Search by name"
        density="comfortable"
        variant="solo-filled"
        flat
        hide-details
        class="search-field"
        @update:model-value="debouncedSearch"
      ></v-text-field>

      <v-card v-if="isSearching" class="search-dropdown" elevation="4">
        <template v-if="visibleConversations.length">
          <div class="search-dropdown__section-label">Open Conversation</div>
          <v-list density="compact" class="search-dropdown__list">
            <v-list-item
              v-for="conversation in visibleConversations"
              :key="conversation.token"
              class="search-dropdown__row"
              @click="selectOpenConversation(conversation)"
            >
              <div class="search-dropdown__row-content">
                <v-avatar
                  v-if="conversation.otherUser.photoUrl"
                  aria-hidden="true"
                  :image="conversation.otherUser.photoUrl"
                  size="36"
                ></v-avatar>
                <v-avatar v-else color="primary" size="36" aria-hidden="true">
                  {{ initials(conversation.otherUser.name) }}
                </v-avatar>
                <div class="search-dropdown__row-body">
                  <div class="font-weight-medium text-body-2">
                    {{ conversation.otherUser.name }}
                  </div>
                  <v-chip size="x-small" variant="outlined" class="product-chip">{{
                    productLabel(conversation)
                  }}</v-chip>
                </div>
              </div>
            </v-list-item>
          </v-list>
        </template>

        <template v-if="newConversationMatches.length">
          <div class="search-dropdown__section-label">New Conversation</div>
          <v-list density="compact" class="search-dropdown__list">
            <v-list-item
              v-for="target in newConversationMatches"
              :key="targetKey(target)"
              class="search-dropdown__row"
              @click="selectNewConversation(target)"
            >
              <div class="search-dropdown__row-content">
                <v-avatar
                  v-if="target.otherUser.photoUrl"
                  aria-hidden="true"
                  :image="target.otherUser.photoUrl"
                  size="36"
                ></v-avatar>
                <v-avatar v-else color="primary" size="36" aria-hidden="true">
                  {{ initials(target.otherUser.name) }}
                </v-avatar>
                <div class="search-dropdown__row-body">
                  <div class="font-weight-medium text-body-2">
                    {{ target.otherUser.name }}
                  </div>
                  <v-chip size="x-small" variant="outlined" class="product-chip">{{
                    productLabelForTarget(target)
                  }}</v-chip>
                </div>
              </div>
            </v-list-item>
          </v-list>
        </template>

        <div
          v-if="!newConversationMatches.length && !visibleConversations.length"
          class="pa-4 text-center text-medium-emphasis"
        >
          No results
        </div>
      </v-card>
    </div>

    <template v-if="!isSearching">
      <v-list class="conversation-list__rows">
        <v-list-item
          v-for="conversation in visibleConversations"
          :key="conversation.token"
          class="conversation-row"
          @click="
            inbox.openConversation({ conversationToken: conversation.token })
          "
        >
          <div class="conversation-row__content">
            <v-avatar
              v-if="conversation.otherUser.photoUrl"
              :image="conversation.otherUser.photoUrl"
              class="conversation-row__avatar"
            ></v-avatar>
            <v-avatar v-else color="primary" class="conversation-row__avatar">
              {{ initials(conversation.otherUser.name) }}
            </v-avatar>

            <div class="conversation-row__body">
              <div class="conversation-row__top">
                <span
                  class="conversation-row__name"
                  :class="{ 'font-weight-bold': conversation.unreadCount > 0 }"
                >
                  {{ conversation.otherUser.name }}
                </span>
                <span class="conversation-row__time text-medium-emphasis">
                  {{ formatTimestamp(conversation.lastMessageCreatedAt) }}
                </span>
                <v-icon
                  v-if="conversation.unreadCount > 0"
                  color="primary"
                  size="8"
                  class="conversation-row__dot"
                  >fa-solid fa-circle</v-icon
                >
              </div>

              <v-chip
                size="x-small"
                variant="outlined"
                class="product-chip"
                :class="{
                  'product-chip--inactive': !conversation.relationshipActive
                }"
                >{{ productLabel(conversation) }}</v-chip
              >

              <div class="conversation-row__preview text-medium-emphasis">
                {{ conversation.lastMessagePreview }}
              </div>
            </div>
          </div>
        </v-list-item>

        <div
          v-if="visibleConversations.length === 0"
          class="text-center text-medium-emphasis pa-6"
        >
          No conversations yet.
        </div>
      </v-list>
    </template>
  </div>
</template>

<script>
import { debounce } from "lodash";
import moment from "moment";
import useInboxState from "@/composables/useInboxState";
import messagingApi from "@/services/messagingApi";

const inbox = useInboxState();

export default {
  name: "ConversationList",
  data() {
    return {
      inbox,
      showUnreadOnly: false,
      filterMenu: false,
      product: null,
      caseState: null,
      search: "",
      polling: false,
      debouncedSearch: null
    };
  },
  computed: {
    visibleConversations() {
      const conversations = this.inbox.state.conversations;
      return this.showUnreadOnly
        ? conversations.filter(c => c.unreadCount > 0)
        : conversations;
    },
    isSearching() {
      return this.search.trim().length > 0;
    },
    newConversationMatches() {
      if (!this.isSearching) return [];
      const q = this.search.trim().toLowerCase();
      return this.inbox.state.eligibleTargets.filter(
        target =>
          !target.existingConversationToken &&
          (target.otherUser.name || "").toLowerCase().includes(q)
      );
    }
  },
  created() {
    this.debouncedSearch = debounce(this.applyFilters, 300);
  },
  mounted() {
    this.refreshList();
    // Only runs while the list is actually mounted (drawer open) - a cheap
    // peek first, only paying for the full re-sorted InboxQuery fetch when
    // something's actually changed.
    this.pollTimer = setInterval(this.poll, 30000);
  },
  beforeUnmount() {
    clearInterval(this.pollTimer);
  },
  methods: {
    async poll() {
      if (this.polling) return;

      this.polling = true;
      try {
        const since = Math.max(
          0,
          ...this.inbox.state.conversations.map(c => c.lastMessageId || 0)
        );
        const { data } = await messagingApi.peekInbox(since);
        if (data.hasNew) this.refreshList();
      } catch {
        // A blip here just means we try again on the next tick - not worth
        // surfacing to the user or letting it become an unhandled rejection.
      } finally {
        this.polling = false;
      }
    },
    refreshList() {
      this.inbox.loadInbox({
        product: this.product,
        caseState: this.product === "uthrive" ? this.caseState : null,
        q: this.search
      });
    },
    applyFilters() {
      this.filterMenu = false;
      this.refreshList();
    },
    targetKey(target) {
      return `${target.otherUser.id}-${target.product}-${target.basicNeedsCase
        ?.id ?? "none"}`;
    },
    productLabelForTarget(target) {
      if (target.product !== "uthrive") return "Therapy";
      return target.basicNeedsCase
        ? `Basic Needs (BN${target.basicNeedsCase.id})`
        : "Basic Needs";
    },
    selectNewConversation(target) {
      this.inbox.openConversation({
        recipientId: target.otherUser.id,
        product: target.product,
        basicNeedsCaseId: target.basicNeedsCase?.id
      });
      this.search = "";
    },
    selectOpenConversation(conversation) {
      this.inbox.openConversation({ conversationToken: conversation.token });
      this.search = "";
    },
    productLabel(conversation) {
      let label = "Therapy";
      if (conversation.product === "uthrive") {
        label = conversation.basicNeedsCase
          ? `Basic Needs (BN${conversation.basicNeedsCase.id})`
          : "Basic Needs";
      }
      if (!conversation.relationshipActive) label += " • Closed";
      return label;
    },
    initials(name) {
      return (name || "")
        .split(" ")
        .map(part => part[0])
        .join("")
        .slice(0, 2)
        .toUpperCase();
    },
    formatTimestamp(value) {
      if (!value) return "No Messages";
      const date = moment(value);
      if (date.isSame(moment(), "day")) return date.format("h:mma");
      if (date.isSame(moment().subtract(1, "day"), "day")) return "Yesterday";
      return date.format("MMM D");
    }
  }
};
</script>

<style lang="scss" scoped>
@use "../../styles/base/variables" as *;

.new-message-btn {
  border-radius: 5px !important;
  padding: 12px !important;
  height: auto !important;
  font-weight: 500;
  margin-bottom: 16px;
}

.filter-row {
  gap: 0;
  padding: 0 12px;
}

.segmented-toggle {
  border-radius: 5px !important;
  overflow: hidden;
  border: 1px solid $uwill-peri-50;
  height: 40px !important;

  :deep(.v-btn) {
    border: none !important;
    color: $uwill-primary-light;
    font-weight: 500;
    text-transform: none;
    letter-spacing: normal;

    &.v-btn--active {
      background-color: $uwill-peri-50;
      color: $uwill-peri;
      font-weight: 700;
    }
  }
}

.filter-btn {
  border-radius: 5px !important;
  border-color: $uwill-peri-50 !important;
  color: $uwill-peri;
  text-transform: none;
  letter-spacing: normal;
  height: 40px !important;
}

.search-wrapper {
  position: relative;
}

.search-field {
  :deep(.v-field),
  :deep(.v-field__overlay) {
    background-color: $uwill-neutral-500 !important;
    opacity: 1 !important;
    border-radius: 5px;
    overflow: hidden;
    box-shadow: none !important;
    border: none !important;
  }
}

.search-dropdown {
  position: absolute;
  top: 100%;
  left: 12px;
  right: 12px;
  z-index: 10;
  max-height: 360px;
  overflow-y: auto;
  border-radius: 8px !important;
}

.search-dropdown__section-label {
  padding: 10px 16px 4px;
  color: $uwill-neutral;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.search-dropdown__list {
  padding: 0;
}

.search-dropdown__row {
  cursor: pointer;
}

.search-dropdown__row-content {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 4px 0;
}

.search-dropdown__row-body {
  flex: 1;
  min-width: 0;
}

.filter-menu-card {
  :deep(.v-label) {
    color: $uwill-peri;
    opacity: 1;
  }

  :deep(.v-selection-control__input .v-icon) {
    color: $uwill-peri;
  }
}

.conversation-row {
  cursor: pointer;
  padding: 12px 4px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}

.conversation-row__content {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  width: 100%;
}

.conversation-row__avatar {
  flex-shrink: 0;
}

.conversation-row__body {
  flex: 1;
  min-width: 0; // required for the ellipsis truncation below to work in a flex child
}

.conversation-row__top {
  display: flex;
  align-items: center;
  gap: 6px;
}

.conversation-row__name {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 13px;
}

.conversation-row__time {
  flex-shrink: 0;
  font-size: 12px;
}

.conversation-row__dot {
  flex-shrink: 0;
}

.conversation-row__preview {
  font-size: 14px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.product-chip {
  border-color: $uwill-peri !important;
  color: $uwill-peri !important;
  margin: 4px 0;
  min-height: auto;

  &--inactive {
    border-color: $uwill-message-pill !important;
    background-color: $uwill-message-pill !important;
    color: $uwill-neutral-500 !important;
  }
}
</style>
